import { detectEnv } from '@/shared/detect';
import yxhJsBridge from '@/shared/yxhJsBridge';
import * as redirector from '@/shared/redirector';
import type VueRouter from 'vue-router';
import { useUserStore } from '@/stores/modules/user';
import { useUTMStore } from '@/stores/modules/utm';
import { useSettingsStore } from '@/stores/modules/settings';
import type { Pinia } from 'pinia';

export default function loginGuard(router: VueRouter, pinia: Pinia) {
  const userStore = useUserStore(pinia);
  const utmStore = useUTMStore(pinia);
  const settingsStore = useSettingsStore(pinia);

  /**
   * 对即将进入的路由判断是否需要登录
   * 当被嵌入到宜享花APP中则使用APP的登录
   */
  if (detectEnv.yxh) {
    router.beforeEach((to, from, next) => {
      if (to.name === 'NotFound' || to.name === 'ChunkLoadError' || to.meta?.excludeSyncUserInfo) {
        next();
        return;
      }

      if (to.meta?.excludeLogin) {
        // 通过获取用户信息来同步APP的登录信息
        yxhJsBridge.getUserInfo().finally(() => {
          next();
        });
      } else {
        yxhJsBridge
          .checkLogin()
          .then(() => {
            next();
          })
          .catch(() => {
            yxhJsBridge.close();
            // next();
          });
      }
    });
  } else {
    router.beforeEach(async (to, from, next) => {
      if (to.name === 'NotFound' || to.name === 'ChunkLoadError') {
        next();
        return;
      }

      // 携带了token的情况下尝试获取用户信息并同步关联三方系统
      if (to.query.token != null) {
        userStore.update({
          token: to.query.token as string,
          isNewUser: to.query.isRegister ? Number(to.query.isRegister) === 1 : undefined,
        });

        try {
          await userStore.fetchProfile({ shouldLogin: false });
          userStore.associateUserWithThirdParty();

          next();
          return;
        } catch {}
        // 启用了活动token的情况下尝试获取用户信息并同步关联三方系统
      } else if (to.meta?.enableActToken && to.query.act_token != null) {
        userStore.updateActToken(to.query.act_token as string);

        try {
          await userStore.fetchProfile({ shouldLogin: false });
          userStore.associateUserWithThirdParty();

          next();
          return;
        } catch {}
      } else {
        userStore.associateUserWithThirdParty();
      }

      if (userStore.isLogin || to.meta?.excludeLogin) {
        next();
        return;
      }

      next({
        path: '/login',
        query: {
          utm_source: utmStore.source,
          destType: settingsStore.destType,
          redirect: redirector.generate(to.fullPath),
        },
        replace: true,
      });
    });
  }
}
